import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, Table, Form, Spinner, Alert, Button, Modal, Tabs, Tab } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DataTable } from '../components/common/DataTable';
import type { ColumnConfig } from '../components/common/DataTable/types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  useGetReportsBurnRate,
  useGetReportsBurnRateTransactions,
  useGetReportsIncome,
  useGetItems,
  useGetUsersId,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import {
  BurnRateBucket,
  GetReportsBurnRateParamsGroupBy,
  GetReportsBurnRateTransactionsParamsSortBy,
  GetReportsBurnRateTransactionsParamsSortDir,
  GetReportsIncomeParamsGroupBy,
  GetReportsIncomeParamsDateField,
  IncomeBucket,
} from '../api/generated/model';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import FinancialReportsFilterBar, { FinancialReportsFiltersState } from '../components/FinancialReportsFilterBar';
import IncomeBreakdownSection, { type BreakdownDimensionKey } from '../components/IncomeBreakdownSection';
import { GetReportsIncomeBreakdownParamsSource } from '../api/generated/model';
import { useTabDateNav } from '../hooks/useTabDateNav';
import DateNavBar from '../components/DateNavBar';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

const formatCurrencyFull = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

const groupByLabel: Record<string, string> = {
  day: 'Day', week: 'Week', month: 'Month', quarter: 'Quarter', year: 'Year',
};

const DEFAULT_FILTERS: FinancialReportsFiltersState = {
  income_date_field: 'invoice_date',
  group_by: 'month',
};

// Map filters.group_by (income enum string) to the burn-rate enum value.
const burnRateGroupBy = (gb: string): GetReportsBurnRateParamsGroupBy => {
  switch (gb) {
    case 'day': return GetReportsBurnRateParamsGroupBy.GetReportsBurnRateParamsGroupByDay;
    case 'week': return GetReportsBurnRateParamsGroupBy.GetReportsBurnRateParamsGroupByWeek;
    case 'quarter': return GetReportsBurnRateParamsGroupBy.GetReportsBurnRateParamsGroupByQuarter;
    case 'year': return GetReportsBurnRateParamsGroupBy.GetReportsBurnRateParamsGroupByYear;
    default: return GetReportsBurnRateParamsGroupBy.GetReportsBurnRateParamsGroupByMonth;
  }
};

const incomeGroupBy = (gb: string): GetReportsIncomeParamsGroupBy => {
  switch (gb) {
    case 'day': return GetReportsIncomeParamsGroupBy.GetReportsIncomeParamsGroupByDay;
    case 'week': return GetReportsIncomeParamsGroupBy.GetReportsIncomeParamsGroupByWeek;
    case 'quarter': return GetReportsIncomeParamsGroupBy.GetReportsIncomeParamsGroupByQuarter;
    case 'year': return GetReportsIncomeParamsGroupBy.GetReportsIncomeParamsGroupByYear;
    default: return GetReportsIncomeParamsGroupBy.GetReportsIncomeParamsGroupByMonth;
  }
};

const incomeDateField = (df: string): GetReportsIncomeParamsDateField => {
  switch (df) {
    case 'due_date': return GetReportsIncomeParamsDateField.GetReportsIncomeParamsDateFieldDueDate;
    case 'booking_date': return GetReportsIncomeParamsDateField.GetReportsIncomeParamsDateFieldBookingDate;
    case 'program_date': return GetReportsIncomeParamsDateField.GetReportsIncomeParamsDateFieldProgramDate;
    default: return GetReportsIncomeParamsDateField.GetReportsIncomeParamsDateFieldInvoiceDate;
  }
};

const FinancialReportsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });

  // Tab is the URL path segment. Each tab owns its own date nav state
  // (via useTabDateNav with a per-tab prefix) so the picker on one
  // tab doesn't bleed into another.
  const validTabs = ['financials', 'program-metrics', 'non-program-metrics'] as const;
  type TabKey = typeof validTabs[number];
  const activeTab: TabKey = (validTabs as readonly string[]).includes(tab || '') ? (tab as TabKey) : 'financials';

  // Default-redirect when the URL has no/invalid tab.
  useEffect(() => {
    if (!tab || !(validTabs as readonly string[]).includes(tab)) {
      navigate('/financial-reports/financials', { replace: true });
    }
  }, [tab, navigate]);

  // Items feed the (shared) filter-bar dropdown; fetch once for the
  // page rather than per-tab.
  const { data: itemsData } = useGetItems();
  const items = useMemo(() => itemsData?.data || [], [itemsData]);

  // Per-tab breakdown dimension. The Program Metrics tab (events
  // source) defaults to State; the Non Program Metrics tab
  // (non_program_lines source) defaults to Customer. Each is
  // persisted to its own URL param so flipping tabs preserves the
  // dimension that was last chosen on each side.
  const [breakdownByProgram, setBreakdownByProgram] = useState<BreakdownDimensionKey>(
    (searchParams.get('breakdown_by_program') as BreakdownDimensionKey) || 'state',
  );
  const [breakdownByNonProgram, setBreakdownByNonProgram] = useState<BreakdownDimensionKey>(
    (searchParams.get('breakdown_by_non_program') as BreakdownDimensionKey) || 'customer',
  );

  const [filters, setFilters] = useState<FinancialReportsFiltersState>(() => ({
    income_date_field: searchParams.get('income_date_field') || DEFAULT_FILTERS.income_date_field,
    group_by: searchParams.get('group_by') || DEFAULT_FILTERS.group_by,
    // For multiselects: null = param absent (default state, all selected).
    // Empty string preserved as the explicit "Deselect all" state — see
    // MultiSelectFilter for the model.
    item_ids: searchParams.get('item_ids') ?? undefined,
    program_names: searchParams.get('program_names') ?? undefined,
    customer_ids: searchParams.get('customer_ids') ?? undefined,
    customer_name: searchParams.get('customer_name') ?? undefined,
    salesperson_names: searchParams.get('salesperson_names') ?? undefined,
    sales_lead_names: searchParams.get('sales_lead_names') ?? undefined,
    state_codes: searchParams.get('state_codes') ?? undefined,
  }));
  const [showFilterBar, setShowFilterBar] = useState(false);

  const groupBy = filters.group_by || 'month';

  // ONE shared date nav across all three tabs. URL keys are
  // unprefixed (`mode`, `year`, etc.). A date selection made on one
  // tab persists when the user switches tabs — that's the explicit
  // contract.
  const dateNav = useTabDateNav({
    prefix: '',
    groupBy: groupBy as any,
  });
  const dateMode = dateNav.dateMode;
  const dateRange = dateNav.dateRange;

  // Sync state to URL params. All state — shared filters, group_by,
  // income_date_field, breakdown dimensions — persists in URL on
  // every tab regardless of which tab is active. That way when the
  // user comes back to a tab, its selections are preserved.
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    // Shared filters across tabs.
    if (filters.item_ids !== undefined) params.set('item_ids', filters.item_ids); else params.delete('item_ids');
    if (filters.program_names !== undefined) params.set('program_names', filters.program_names); else params.delete('program_names');
    if (filters.customer_ids !== undefined) params.set('customer_ids', filters.customer_ids); else params.delete('customer_ids');
    if (filters.customer_name) params.set('customer_name', filters.customer_name); else params.delete('customer_name');
    if (filters.salesperson_names !== undefined) params.set('salesperson_names', filters.salesperson_names); else params.delete('salesperson_names');
    if (filters.sales_lead_names !== undefined) params.set('sales_lead_names', filters.sales_lead_names); else params.delete('sales_lead_names');
    if (filters.state_codes !== undefined) params.set('state_codes', filters.state_codes); else params.delete('state_codes');
    if (filters.income_date_field) params.set('income_date_field', filters.income_date_field); else params.delete('income_date_field');
    params.set('group_by', groupBy);

    // Per-tab dimensions persisted on every tab so returning to a tab
    // restores its prior selection.
    params.set('breakdown_by_program', breakdownByProgram);
    params.set('breakdown_by_non_program', breakdownByNonProgram);

    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, filters, breakdownByProgram, breakdownByNonProgram, setSearchParams]);

  const burnRateEnabled = activeTab === 'financials' && (dateMode === 'all' || (!!dateRange.start_date && !!dateRange.end_date));

  const { data: burnRateData, isLoading: burnRateLoading, error: burnRateError } = useGetReportsBurnRate(
    {
      // 'all' mode emits empty strings; convert to undefined so orval drops
      // the params from the URL — backend reads "missing" as no bound.
      start_date: dateRange.start_date || undefined,
      end_date: dateRange.end_date || undefined,
      group_by: burnRateGroupBy(groupBy),
    } as any,
    { query: { enabled: burnRateEnabled } }
  );

  const buckets: BurnRateBucket[] = useMemo(() => burnRateData?.buckets || [], [burnRateData]);
  const summary = burnRateData?.summary;

  // Income — single call to /reports/income; replaces the old N+1 getInvoices loop.
  const incomeParams = useMemo(() => ({
    // 'all' mode emits empty strings; convert to undefined so orval drops
    // the params from the URL — backend reads "missing" as no bound.
    start_date: dateRange.start_date || undefined,
    end_date: dateRange.end_date || undefined,
    date_field: incomeDateField(filters.income_date_field || 'invoice_date'),
    group_by: incomeGroupBy(groupBy),
    item_ids: filters.item_ids,
    program_names: filters.program_names,
    customer_ids: filters.customer_ids,
    customer_name: filters.customer_name,
    salesperson_names: filters.salesperson_names,
    sales_lead_names: filters.sales_lead_names,
    state_codes: filters.state_codes,
    sort_field: 'date',
    sort_direction: 'asc' as const,
    limit: 100,
  }), [dateRange.start_date, dateRange.end_date, groupBy, filters]);

  const { data: incomeData, isLoading: incomeLoading, error: incomeError } = useGetReportsIncome(
    incomeParams as any,
    { query: { enabled: burnRateEnabled } }
  );

  // Index income buckets by start_date so we can look them up alongside burn-rate buckets.
  const incomeByStart = useMemo(() => {
    const map: Record<string, IncomeBucket> = {};
    (incomeData?.data || []).forEach(b => { if (b.start_date) map[b.start_date] = b; });
    return map;
  }, [incomeData]);

  // Bucket table sort state — keyed on column key so any column can sort.
  const [bucketSortBy, setBucketSortBy] = useState<string>('start_date');
  const [bucketSortAsc, setBucketSortAsc] = useState(true);

  // Merge burn-rate buckets with the corresponding income bucket (matched
  // by start_date — both endpoints emit natural-aligned starts) so each
  // row owns all the data renderCell needs. DataTable's renderCell is
  // captured once at mount; reading external state from inside it leaves
  // closures stale forever, so the merge has to happen here.
  const mergedRows = useMemo(() => {
    const f = (s?: string | null) => parseFloat(s || '0');
    return buckets.map(b => {
      const inc = incomeByStart[b.start_date];
      return {
        ...b,
        income_gross: f(inc?.total_gross),
        income_discount: f(inc?.total_discount),
        income_net: f(inc?.total_net),
        income_net_revenue: f(inc?.total_net_revenue),
        income_collected: f(inc?.total_collected),
        income_balance_due: f(inc?.total_balance_due),
        income_event_count: inc?.event_count ?? 0,
      };
    });
  }, [buckets, incomeByStart]);

  const sortedBuckets = useMemo(() => {
    const sorted = [...mergedRows];
    sorted.sort((a, b) => {
      const av = (a as any)[bucketSortBy];
      const bv = (b as any)[bucketSortBy];
      let cmp: number;
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else {
        cmp = String(av ?? '').localeCompare(String(bv ?? ''));
      }
      return bucketSortAsc ? cmp : -cmp;
    });
    return sorted;
  }, [mergedRows, bucketSortBy, bucketSortAsc]);

  // Drill-down state
  const [selectedBucket, setSelectedBucket] = useState<BurnRateBucket | null>(null);
  const [txSortBy, setTxSortBy] = useState<GetReportsBurnRateTransactionsParamsSortBy>(GetReportsBurnRateTransactionsParamsSortBy.GetReportsBurnRateTransactionsParamsSortByDate);
  const [txSortDir, setTxSortDir] = useState<GetReportsBurnRateTransactionsParamsSortDir>(GetReportsBurnRateTransactionsParamsSortDir.GetReportsBurnRateTransactionsParamsSortDirDesc);

  const { data: transactions, isLoading: txLoading } = useGetReportsBurnRateTransactions(
    {
      start_date: selectedBucket?.start_date || '',
      end_date: selectedBucket?.end_date || '',
      sort_by: txSortBy,
      sort_dir: txSortDir,
    },
    { query: { enabled: !!selectedBucket } }
  );

  const handleBucketClick = (bucket: BurnRateBucket) => {
    setSelectedBucket(bucket);
    setTxSortBy(GetReportsBurnRateTransactionsParamsSortBy.GetReportsBurnRateTransactionsParamsSortByDate);
    setTxSortDir(GetReportsBurnRateTransactionsParamsSortDir.GetReportsBurnRateTransactionsParamsSortDirDesc);
  };

  const handleTxSort = (field: GetReportsBurnRateTransactionsParamsSortBy) => {
    if (txSortBy === field) {
      setTxSortDir(d => d === GetReportsBurnRateTransactionsParamsSortDir.GetReportsBurnRateTransactionsParamsSortDirAsc
        ? GetReportsBurnRateTransactionsParamsSortDir.GetReportsBurnRateTransactionsParamsSortDirDesc
        : GetReportsBurnRateTransactionsParamsSortDir.GetReportsBurnRateTransactionsParamsSortDirAsc);
    } else {
      setTxSortBy(field);
      setTxSortDir(GetReportsBurnRateTransactionsParamsSortDir.GetReportsBurnRateTransactionsParamsSortDirDesc);
    }
  };

  const sortIcon = (field: GetReportsBurnRateTransactionsParamsSortBy) => {
    if (txSortBy !== field) return 'bi-chevron-expand';
    return txSortDir === GetReportsBurnRateTransactionsParamsSortDir.GetReportsBurnRateTransactionsParamsSortDirAsc ? 'bi-chevron-up' : 'bi-chevron-down';
  };

  const handleClearFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS });
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.income_date_field) count++;
    if (filters.group_by) count++;
    if (filters.item_ids !== undefined) count++;
    if (filters.program_names !== undefined) count++;
    if (filters.customer_ids !== undefined) count++;
    if (filters.customer_name) count++;
    if (filters.salesperson_names !== undefined) count++;
    if (filters.sales_lead_names !== undefined) count++;
    if (filters.state_codes !== undefined) count++;
    return count;
  }, [filters]);

  // Chart bar = net amount, decomposed into Collected + Balance Due so
  // the stack lines up with cash flow. Gross / Net Revenue show in the
  // summary card only — keeping the chart to one income measure.
  const chartData = buckets.map(b => {
    const inc = incomeByStart[b.start_date];
    const balance = inc?.total_balance_due ? parseFloat(inc.total_balance_due) : 0;
    const collected = inc?.total_collected ? parseFloat(inc.total_collected) : 0;
    return {
      label: b.label,
      amount: b.total_outgoing,
      collected: Math.round(collected * 100) / 100,
      balanceDue: Math.round(balance * 100) / 100,
      bucket: b,
    };
  });

  const isLoading = burnRateLoading || incomeLoading;

  // Date nav + filter toggle + filter bar, rendered inside each tab's
  // Card.Header (the canonical pattern — see comment near Tabs). State
  // is shared at the page level, so the controls show the same
  // selection on every tab.
  const tabHeaderControls = (
    <>
      <DateNavBar
        nav={dateNav}
        leftLabel={<><i className="bi bi-calendar-range me-1"></i>Date</>}
        inlineExtra={
          <Button
            variant={activeFilterCount > 0 ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => setShowFilterBar(s => !s)}
          >
            <i className="bi bi-funnel me-1"></i>
            Filters
            {activeFilterCount > 0 && (
              <span className="badge bg-light text-primary ms-1">{activeFilterCount}</span>
            )}
          </Button>
        }
      />
      {showFilterBar && (
        <FinancialReportsFilterBar
          filters={filters}
          items={items}
          onFiltersChange={setFilters}
          onClearFilters={handleClearFilters}
        />
      )}
    </>
  );

  return (
    <DashboardLayout title="Financial Reports" subtitle="Burn rate and spending analysis" scrollable>
      {burnRateError && (
        <Alert variant="danger" className="mt-3">
          {(burnRateError as any)?.response?.data?.message || 'Failed to load burn rate data'}
        </Alert>
      )}

      {incomeError && (
        <Alert variant="danger" className="mt-3">
          {(incomeError as any)?.response?.data?.message || 'Failed to load income data'}
        </Alert>
      )}

      {isLoading && !burnRateData && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {/* Date nav + filter bar live INSIDE each tab's Card.Header
          (the canonical filter-bar-in-card pattern other pages use).
          FilterControls' MultiSelectFilter caps the dropdown's
          max-height to the remaining space inside the closest .card,
          so the filter bar MUST share a card with the table or the
          dropdown collapses to a sliver. State is page-level and
          shared, so the controls render the same selection regardless
          of which tab is active. */}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => k && navigate(`/financial-reports/${k}`)}
        // mountOnEnter / unmountOnExit per feedback_tabs_mount_on_enter —
        // each tab's queries only run when the tab is active. Switching
        // tabs cancels stale fetches.
        mountOnEnter
        unmountOnExit
        className="flex-shrink-0"
      >
        <Tab eventKey="financials" title="Financials">
        {burnRateData && summary && (
        <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, overflow: 'auto' }}>
          {/* Summary Cards — flex-row so each card hugs its content;
              flex-wrap so they reflow on narrow viewports. */}
          <div className="d-flex gap-3 mb-3 mt-3 flex-shrink-0 flex-wrap align-items-stretch">
            <Card>
              <Card.Body className="text-center">
                <div className="text-muted small mb-1">Expenses</div>
                <div className="fs-3 fw-bold text-danger" style={{ whiteSpace: 'nowrap' }}>{formatCurrencyFull(summary.total_outgoing)}</div>
                <div className="small text-warning" style={{ whiteSpace: 'nowrap' }}>
                  Avg per {groupByLabel[groupBy]}{summary.complete_bucket_count < summary.bucket_count
                    ? ` (${summary.complete_bucket_count} complete)`
                    : ''}: {formatCurrencyFull(summary.average_per_bucket)}
                </div>
              </Card.Body>
            </Card>
            <Card>
              <Card.Body className="p-2 d-flex align-items-center">
                  {/* Left column = the income derivation chain
                        (Gross − Discount = Net Amount; minus non-commissionable
                        lines = Net Revenue). Right column = AR breakdown of
                        Net Amount. The right column is intentionally blank on
                        the bottom two rows so the chain reads top-to-bottom
                        without the Net rows being pulled to the right. */}
                  <table className="table table-sm table-borderless mb-0" style={{ whiteSpace: 'nowrap' }}>
                    <tbody>
                      <tr>
                        <td className="text-muted">Gross</td>
                        <td className="text-end fw-semibold" style={{ paddingRight: '1rem', fontFamily: 'monospace' }}>
                          {formatCurrencyFull(parseFloat(incomeData?.summary?.total_gross || '0'))}
                        </td>
                        <td className="text-muted" style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '1rem' }}>Collected</td>
                        <td className="text-end fw-semibold text-success" style={{ fontFamily: 'monospace' }}>
                          {formatCurrencyFull(parseFloat(incomeData?.summary?.total_collected || '0'))}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Discount</td>
                        <td className="text-end text-danger" style={{ paddingRight: '1rem', fontFamily: 'monospace' }}>
                          −{formatCurrencyFull(parseFloat(incomeData?.summary?.total_discount || '0'))}
                        </td>
                        <td className="text-muted" style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '1rem' }}>Balance Due</td>
                        <td className="text-end fw-semibold text-warning" style={{ fontFamily: 'monospace' }}>
                          {formatCurrencyFull(parseFloat(incomeData?.summary?.total_balance_due || '0'))}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Net Amount</td>
                        <td className="text-end fw-semibold" style={{ paddingRight: '1rem', fontFamily: 'monospace' }}>
                          {formatCurrencyFull(parseFloat(incomeData?.summary?.total_net || '0'))}
                        </td>
                        <td style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '1rem' }}></td>
                        <td></td>
                      </tr>
                      <tr>
                        <td className="text-muted">Net Revenue</td>
                        <td className="text-end fw-semibold text-success" style={{ paddingRight: '1rem', fontFamily: 'monospace' }}>
                          {formatCurrencyFull(parseFloat(incomeData?.summary?.total_net_revenue || '0'))}
                        </td>
                        <td style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '1rem' }}></td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </Card.Body>
            </Card>
            <Card>
              <Card.Body className="text-center">
                <div className="text-muted small mb-1" style={{ whiteSpace: 'nowrap' }}>{groupByLabel[groupBy]}s in Period</div>
                <div className="fs-3 fw-bold">{summary.bucket_count}</div>
              </Card.Body>
            </Card>
            {/* Events stat — only meaningful when bucket axis is program_date
                (each event = one events row joined into the bucket). For
                invoice-level date modes the backend returns 0, so hide. */}
            {filters.income_date_field === 'program_date' && (
              <Card>
                <Card.Body className="text-center">
                  <div className="text-muted small mb-1" style={{ whiteSpace: 'nowrap' }}>Events in Period</div>
                  <div className="fs-3 fw-bold">{incomeData?.summary?.total_event_count ?? 0}</div>
                </Card.Body>
              </Card>
            )}
          </div>

          {/* Chart + breakdown table. Date controls + filters live
              inside this card's header (canonical pattern). */}
          <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '340px', overflow: 'hidden' }}>
            <Card.Header className="bg-primary text-white flex-shrink-0">
              {tabHeaderControls}
            </Card.Header>

            <Card.Body className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
              {/* Chart */}
              {chartData.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Spend &amp; Net Amount by {groupByLabel[groupBy]}</h6>
                  </div>
                  <div style={{ width: '100%', height: 350, minHeight: 350, minWidth: 400 }}>
                    <ResponsiveContainer>
                      <BarChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={v => formatCurrency(v)} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(value: number, name: string) => {
                          const labels: Record<string, string> = {
                            amount: 'Spend',
                            collected: 'Collected',
                            balanceDue: 'Balance Due',
                          };
                          return [formatCurrencyFull(value), labels[name] || name];
                        }} />
                        <Legend formatter={(value: string) => {
                          const labels: Record<string, string> = {
                            amount: 'Spend',
                            collected: 'Collected',
                            balanceDue: 'Balance Due',
                          };
                          return labels[value] || value;
                        }} />
                        <Bar
                          dataKey="amount"
                          fill="#dc3545"
                          radius={[4, 4, 0, 0]}
                          cursor="pointer"
                          onClick={(data: any) => { if (data?.bucket) handleBucketClick(data.bucket); }}
                        />
                        <Bar dataKey="collected" stackId="income" fill="#198754" />
                        <Bar dataKey="balanceDue" stackId="income" fill="#ffc107" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Breakdown Table */}
              <DataTable
                data={sortedBuckets}
                columns={[
                  { key: 'label', label: 'Period', width: 150, sortable: true },
                  { key: 'start_date', label: 'Start', width: 120, sortable: true },
                  { key: 'end_date', label: 'End', width: 120, sortable: true },
                  {
                    key: 'total_outgoing', label: 'Expenses', width: 130, sortable: true,
                    renderCell: (b: any) => (
                      <span
                        className="d-block text-end text-primary"
                        style={{ fontFamily: 'monospace', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={(e) => { e.stopPropagation(); handleBucketClick(b); }}
                        title={`View ${b.transaction_count} transaction${b.transaction_count === 1 ? '' : 's'}`}
                      >
                        {formatCurrencyFull(b.total_outgoing)}
                      </span>
                    ),
                  },
                  {
                    key: 'income_gross', label: 'Gross', width: 130, sortable: true,
                    renderCell: (b: any) => <span className="d-block text-end" style={{ fontFamily: 'monospace' }}>{formatCurrencyFull(b.income_gross)}</span>,
                  },
                  {
                    key: 'income_discount', label: 'Discount', width: 120, sortable: true,
                    renderCell: (b: any) => <span className="d-block text-end text-danger" style={{ fontFamily: 'monospace' }}>{b.income_discount ? `−${formatCurrencyFull(b.income_discount)}` : formatCurrencyFull(0)}</span>,
                  },
                  {
                    key: 'income_net', label: 'Net Amount', width: 130, sortable: true,
                    renderCell: (b: any) => <span className="d-block text-end" style={{ fontFamily: 'monospace' }}>{formatCurrencyFull(b.income_net)}</span>,
                  },
                  {
                    key: 'income_net_revenue', label: 'Net Revenue', width: 130, sortable: true,
                    renderCell: (b: any) => <span className="d-block text-end text-success" style={{ fontFamily: 'monospace' }}>{formatCurrencyFull(b.income_net_revenue)}</span>,
                  },
                  {
                    key: 'income_collected', label: 'Collected', width: 130, sortable: true,
                    renderCell: (b: any) => <span className="d-block text-end text-success" style={{ fontFamily: 'monospace' }}>{formatCurrencyFull(b.income_collected)}</span>,
                  },
                  {
                    key: 'income_balance_due', label: 'Balance Due', width: 130, sortable: true,
                    renderCell: (b: any) => <span className="d-block text-end text-warning" style={{ fontFamily: 'monospace' }}>{formatCurrencyFull(b.income_balance_due)}</span>,
                  },
                  // Events column — only meaningful in program_date mode (each
                  // events row = one join into the bucket). For invoice-level
                  // date modes the backend returns 0. Column is always
                  // present (DataTable captures columns once via useState —
                  // conditional columns drift stale); render '—' when
                  // program_date isn't the bucket axis.
                  {
                    key: 'income_event_count', label: 'Events', width: 90, sortable: true,
                    renderCell: (b: any) => (
                      <span className="d-block text-end" style={{ fontFamily: 'monospace' }}>
                        {filters.income_date_field === 'program_date' ? b.income_event_count : '—'}
                      </span>
                    ),
                  },
                ] as ColumnConfig<any>[]}
                getRowKey={(b: any) => b.label}
                sortConfig={{ field: bucketSortBy, direction: bucketSortAsc ? 'asc' : 'desc' }}
                onSortChange={(field, dir) => { setBucketSortBy(field); setBucketSortAsc(dir === 'asc'); }}
                enableColumnReorder={true}
                enableColumnResize={true}
                enableColumnVisibility={true}
                enableStickyHeader={true}
                preferencesKey="financial_breakdown"
                userId={user?.id}
                userPreferences={userData?.preferences}
                maxHeight="100%"
                emptyState={<div className="text-center text-muted py-3">No transaction data for this period</div>}
              />
            </Card.Body>
          </Card>

        </div>
        )}
        </Tab>
        <Tab eventKey="program-metrics" title="Program Metrics">
          {/* Tab content wrapped per feedback_datatable_layout — the
              flex chain from layout → tab pane → wrapper → Card →
              Card.Body → DataTable is what makes the table fill the
              viewport. */}
          <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, overflow: 'auto' }}>
            <IncomeBreakdownSection
              dateRange={dateRange}
              filters={filters}
              dimension={breakdownByProgram}
              onDimensionChange={setBreakdownByProgram}
              headerPrefix={tabHeaderControls}
              userId={user?.id}
              userPreferences={userData?.preferences}
            />
          </div>
        </Tab>
        <Tab eventKey="non-program-metrics" title="Non Program Metrics">
          <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, overflow: 'auto' }}>
            <IncomeBreakdownSection
              dateRange={dateRange}
              filters={filters}
              dimension={breakdownByNonProgram}
              onDimensionChange={setBreakdownByNonProgram}
              source={GetReportsIncomeBreakdownParamsSource.GetReportsIncomeBreakdownParamsSourceNonProgramLines}
              allowedDimensions={['customer', 'item']}
              countLabel="Lines"
              headerPrefix={tabHeaderControls}
              userId={user?.id}
              userPreferences={userData?.preferences}
            />
          </div>
        </Tab>
      </Tabs>

      {/* Date Picker Modal — provided by DateNavBar (one per tab nav). */}

      {/* Transaction Detail Modal */}
      <Modal show={!!selectedBucket} onHide={() => setSelectedBucket(null)} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Transactions — {selectedBucket?.label}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {txLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          )}
          {!txLoading && transactions && (
            <Table striped hover responsive size="sm">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleTxSort(GetReportsBurnRateTransactionsParamsSortBy.GetReportsBurnRateTransactionsParamsSortByDate)}>
                    Date <i className={`bi ${sortIcon(GetReportsBurnRateTransactionsParamsSortBy.GetReportsBurnRateTransactionsParamsSortByDate)} ms-1`}></i>
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleTxSort(GetReportsBurnRateTransactionsParamsSortBy.GetReportsBurnRateTransactionsParamsSortByCounterparty)}>
                    Counterparty <i className={`bi ${sortIcon(GetReportsBurnRateTransactionsParamsSortBy.GetReportsBurnRateTransactionsParamsSortByCounterparty)} ms-1`}></i>
                  </th>
                  <th style={{ cursor: 'pointer' }} className="text-end text-nowrap" onClick={() => handleTxSort(GetReportsBurnRateTransactionsParamsSortBy.GetReportsBurnRateTransactionsParamsSortByAmount)}>
                    Amount <i className={`bi ${sortIcon(GetReportsBurnRateTransactionsParamsSortBy.GetReportsBurnRateTransactionsParamsSortByAmount)} ms-1`}></i>
                  </th>
                  <th>Note</th>
                  <th>Recipient Memo</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(tx => (
                  <tr key={tx.id}>
                    <td className="text-nowrap">{tx.date}</td>
                    <td>
                      {tx.dashboard_link ? (
                        <a href={tx.dashboard_link} target="_blank" rel="noopener noreferrer">{tx.counterparty_name}</a>
                      ) : tx.counterparty_name}
                    </td>
                    <td className="text-end text-nowrap" style={{ fontFamily: 'monospace' }}>{formatCurrencyFull(tx.amount)}</td>
                    <td className="text-muted small">{tx.note}</td>
                    <td className="text-muted small">{tx.recipient_memo}</td>
                  </tr>
                ))}
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-3">No transactions</td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>
    </DashboardLayout>
  );
};

export default FinancialReportsPage;
